<script lang="ts">
  import { onMount } from 'svelte';
  import { ApiError, type Message } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { render } from '../lib/markdown';
  import { messagePath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let error = $state('');
  let more = $state(true);

  onMount(() => {
    void app.loadSaved().catch(() => (error = 'Impossible de charger les messages enregistrés'));
  });

  function where(message: Message): string {
    const c = app.conversation(message.conversation_id);
    if (!c) return '';
    return c.kind === 'public' || c.kind === 'private' ? `#${c.title}` : c.title;
  }

  async function loadMore() {
    try {
      more = await app.loadSaved(true);
    } catch {
      error = 'Impossible de charger la suite';
    }
  }

  async function unsave(message: Message) {
    try {
      await app.toggleSaved(message);
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }
</script>

<section class="page" aria-labelledby="saved-title">
  <header>
    <button type="button" class="back" aria-label="Retour aux conversations" onclick={() => router.go('/')}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <div>
      <h1 id="saved-title">Messages enregistrés</h1>
      <p class="sub">{app.saved ? `${app.saved.length}${more && app.saved.length >= 50 ? '+' : ''} message${app.saved.length > 1 ? 's' : ''} · ` : ''}visibles par vous seul</p>
    </div>
  </header>
  <div class="list">
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if !app.saved}
      <p class="status">Chargement…</p>
    {:else}
      {#each app.saved as item (item.message.id)}
        {@const message = item.message}
        <article class="card">
          <div class="meta">
            <Avatar user={message.author} size={28} />
            <span class="author">{message.author.display_name}</span>
            <span class="where">{where(message)} · {dayAndTime(message.created_at)}</span>
          </div>
          <div class="body">{@html render(message.body, app.me?.username ?? '')}</div>
          {#if message.attachments.length > 0}
            <p class="files">{message.attachments.map((a) => a.name).join(', ')}</p>
          {/if}
          <div class="actions">
            <button type="button" class="link" onclick={() => router.go(messagePath(message.conversation_id, message.id))}>Voir dans la conversation</button>
            <button type="button" class="link" onclick={() => unsave(message)}>Retirer</button>
          </div>
        </article>
      {:else}
        <p class="status">Aucun message enregistré. Utilisez le signet dans le menu d'un message pour le retrouver ici.</p>
      {/each}
      {#if more && app.saved.length >= 50}
        <button type="button" class="btn" onclick={loadMore}>Afficher plus</button>
      {/if}
      <p class="note">Les messages d'une conversation que vous avez quittée n'apparaissent pas ici ; ils reviennent si vous la rejoignez.</p>
    {/if}
  </div>
</section>

<style>
  .page {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-page);
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: var(--header-h);
    padding: 8px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  h1 {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
  }
  .sub {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .back {
    display: none;
    width: 44px;
    height: 44px;
    margin-left: -12px;
    border: 0;
    background: transparent;
    color: var(--accent-text);
    align-items: center;
    justify-content: center;
  }
  .list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 820px;
    width: 100%;
  }
  .card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .author {
    font-weight: 600;
  }
  .where {
    font-size: 12px;
    color: var(--muted);
  }
  .body {
    overflow-wrap: anywhere;
  }
  .body :global(p) {
    margin: 0;
  }
  .files {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .actions {
    display: flex;
    gap: 16px;
  }
  .link {
    border: 0;
    background: none;
    padding: 0;
    color: var(--accent-text);
    font-weight: 600;
    font-size: 13px;
  }
  .status,
  .note {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
  .btn {
    align-self: flex-start;
  }
  @media (max-width: 759px) {
    header {
      padding: 8px 12px;
    }
    .back {
      display: flex;
    }
    .list {
      padding: 12px;
    }
  }
</style>
